import { GraduationCap } from 'lucide-react';
import {
  SKILLS,
  SKILL_KEYS,
  coursesOf,
  type Course,
  type CoursesContent,
  type Lesson,
  type SkillKey,
} from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Switch } from '@london-life/ui/components/switch';
import { Textarea } from '@london-life/ui/components/textarea';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, idFor, type NumberSpec } from './form';

type CourseNumbers = Exclude<keyof CoursesContent, 'visible' | 'placeId' | 'classActivityId' | 'courses'>;
const RULES: NumberSpec<CourseNumbers>[] = [
  { key: 'classesPerDay', label: 'Classes a day', unit: 'number', min: 1, max: 10, whole: true },
  { key: 'questionsPerQuiz', label: 'Questions in a quiz', unit: 'number', min: 1, max: 20, whole: true },
  { key: 'passMark', label: 'Right answers to pass', unit: 'number', min: 1, max: 20, whole: true },
  { key: 'missesAllowed', label: 'Wrong answers before the quiz stops', unit: 'number', min: 0, max: 20, whole: true },
  {
    key: 'retryMinutes',
    label: 'Minutes before a failed quiz reopens',
    unit: 'number',
    min: 0,
    max: 1440,
    whole: true,
  },
];
const REWARDS: NumberSpec<CourseNumbers>[] = [
  { key: 'lessonXp', label: 'Skill XP for a lesson passed', unit: 'number', min: 0, max: 1000, whole: true },
  { key: 'lessonWishPoints', label: 'Wish points for a lesson', unit: 'number', min: 0, max: 100, whole: true },
  {
    key: 'certificateWishPoints',
    label: 'Wish points for a certificate',
    unit: 'number',
    min: 0,
    max: 500,
    whole: true,
  },
  { key: 'certificateXpBoost', label: 'A certificate: its skill grows faster by', unit: 'percent', min: 0, max: 2 },
  {
    key: 'certificateCareerBoost',
    label: 'A certificate: fewer shifts for each promotion',
    unit: 'percent',
    min: 0,
    max: 0.9,
  },
];
const FEE: NumberSpec<'feePence'> = { key: 'feePence', label: 'Fee', unit: 'pounds', min: 0, max: 100_000_000 };
// notes are paragraphs: a blank line between them
const paragraphs = (text: string) =>
  text
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);

// Courses (as Lagos Life's Courses): the rules for classes and quizzes, what lessons and certificates are worth,
// and every course with its lessons, notes and questions. Part of the content draft, published with it.
export function CoursesPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const courses = coursesOf(working ?? {});
  const change = (fn: (draft: CoursesContent) => void) =>
    editor.edit((content) => {
      content.courses ??= structuredClone(coursesOf(content));
      fn(content.courses);
    });
  const course = (id: string, fn: (entry: Course) => void) =>
    change((draft) => {
      const entry = draft.courses.find((item) => item.id === id);
      if (entry) fn(entry);
    });
  const numbers = (specs: NumberSpec<CourseNumbers>[]) =>
    specs.map((spec) => (
      <NumberField
        key={spec.key}
        id={`courses-${spec.key}`}
        spec={spec}
        value={courses[spec.key]}
        onChange={(value) => change((draft) => void (draft[spec.key] = value))}
      />
    ));
  const jobs = (working?.activities ?? []).filter((activity) => activity.job);
  const placeActivities = (working?.activities ?? []).filter((activity) => activity.placeId === courses.placeId);
  return (
    <section>
      <PageHeader
        title="Courses"
        description="Courses players study (as Lagos Life's Courses): classes, notes, quizzes and certificates. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Classes and quizzes">
            <h2 className="content-subtitle">
              <GraduationCap size={16} aria-hidden="true" /> Classes and quizzes
            </h2>
            <label className="designer-check">
              <Switch
                checked={courses.visible}
                aria-label="Courses open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can enrol and study
            </label>
            <div className="content-grid">
              <div className="admin-field">
                <Label htmlFor="courses-place">Classes are at</Label>
                <select
                  id="courses-place"
                  className="content-select"
                  value={courses.placeId}
                  onChange={(e) => change((draft) => void (draft.placeId = e.target.value))}
                >
                  {working.places.map((place) => (
                    <option key={place.id} value={place.id}>
                      {place.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-field">
                <Label htmlFor="courses-class">The class (an activity there)</Label>
                <select
                  id="courses-class"
                  className="content-select"
                  value={courses.classActivityId}
                  onChange={(e) => change((draft) => void (draft.classActivityId = e.target.value))}
                >
                  {!placeActivities.some((activity) => activity.id === courses.classActivityId) && (
                    <option value={courses.classActivityId}>Choose one</option>
                  )}
                  {placeActivities.map((activity) => (
                    <option key={activity.id} value={activity.id}>
                      {activity.label}
                    </option>
                  ))}
                </select>
              </div>
              {numbers(RULES)}
            </div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Rewards">
            <h2 className="content-subtitle">Rewards</h2>
            <div className="content-grid">{numbers(REWARDS)}</div>
          </fieldset>
          {courses.courses.map((item) => (
            <fieldset
              key={item.id}
              className="admin-panel content-section"
              disabled={readOnly}
              aria-label={item.label}
              data-testid={`course-${item.id}`}
            >
              <h2 className="content-subtitle">
                {item.emoji} {item.label}
              </h2>
              <div className="content-grid">
                <Field id={`course-${item.id}-label`} label="Name">
                  <Input
                    id={`course-${item.id}-label`}
                    value={item.label}
                    maxLength={60}
                    onChange={(e) => course(item.id, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`course-${item.id}-emoji`} label="Emoji">
                  <Input
                    id={`course-${item.id}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => course(item.id, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <NumberField
                  id={`course-${item.id}-fee`}
                  spec={FEE}
                  value={item.feePence}
                  onChange={(value) => course(item.id, (entry) => void (entry.feePence = value))}
                />
                <div className="admin-field">
                  <Label htmlFor={`course-${item.id}-skill`}>Skill</Label>
                  <select
                    id={`course-${item.id}-skill`}
                    className="content-select"
                    value={item.skill}
                    onChange={(e) => course(item.id, (entry) => void (entry.skill = e.target.value as SkillKey))}
                  >
                    {SKILL_KEYS.map((skill) => (
                      <option key={skill} value={skill}>
                        {SKILLS[skill].icon} {SKILLS[skill].label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-field">
                  <Label htmlFor={`course-${item.id}-career`}>Job it helps with</Label>
                  <select
                    id={`course-${item.id}-career`}
                    className="content-select"
                    value={item.career ?? ''}
                    onChange={(e) => course(item.id, (entry) => void (entry.career = e.target.value || null))}
                  >
                    <option value="">None (a life skill)</option>
                    {jobs.map((job) => (
                      <option key={job.id} value={job.id}>
                        {job.label} ({working.places.find((place) => place.id === job.placeId)?.name ?? job.placeId})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Field id={`course-${item.id}-blurb`} label="What it is">
                <Textarea
                  id={`course-${item.id}-blurb`}
                  value={item.blurb}
                  maxLength={240}
                  onChange={(e) => course(item.id, (entry) => void (entry.blurb = e.target.value))}
                />
              </Field>
              <label className="designer-check">
                <Switch
                  checked={item.visible}
                  aria-label={`${item.label} on offer`}
                  onCheckedChange={(on) => course(item.id, (entry) => void (entry.visible = on))}
                />{' '}
                On offer (players already enrolled keep it)
              </label>
              {item.lessons.map((lesson, index) => (
                <LessonEditor
                  key={index}
                  id={`course-${item.id}-lesson-${index}`}
                  number={index + 1}
                  lesson={lesson}
                  onChange={(fn) => course(item.id, (entry) => fn(entry.lessons[index]!))}
                />
              ))}
            </fieldset>
          ))}
          <Button
            type="button"
            variant="outline"
            disabled={readOnly}
            onClick={() =>
              change((draft) =>
                draft.courses.push({
                  id: idFor('New course', new Set(draft.courses.map((entry) => entry.id)), 'course', 30),
                  label: 'New course',
                  emoji: '🎓',
                  blurb: 'What the course is about and who it is for.',
                  skill: 'charisma',
                  career: null,
                  feePence: 3000,
                  lessons: [
                    {
                      title: 'Lesson one',
                      notes: ['What the lesson teaches.'],
                      questions: Array.from({ length: Math.max(draft.questionsPerQuiz, draft.passMark) }, (_, n) => ({
                        q: `Question ${n + 1}?`,
                        right: 'The right answer',
                        wrong: ['A wrong answer', 'Another wrong answer', 'A third wrong answer'],
                      })),
                    },
                  ],
                  visible: false,
                }),
              )
            }
          >
            Add a course
          </Button>
        </div>
      )}
    </section>
  );
}

function LessonEditor({
  id,
  number,
  lesson,
  onChange,
}: {
  id: string;
  number: number;
  lesson: Lesson;
  onChange: (fn: (lesson: Lesson) => void) => void;
}) {
  return (
    <details className="content-details" data-testid={id}>
      <summary>
        Lesson {number}: {lesson.title} · {lesson.questions.length} questions
      </summary>
      <Field id={`${id}-title`} label="Title">
        <Input
          id={`${id}-title`}
          value={lesson.title}
          maxLength={80}
          onChange={(e) => onChange((entry) => void (entry.title = e.target.value))}
        />
      </Field>
      <Field id={`${id}-notes`} label="Notes (a blank line between paragraphs)">
        <Textarea
          id={`${id}-notes`}
          rows={6}
          defaultValue={lesson.notes.join('\n\n')}
          onBlur={(e) => {
            const notes = paragraphs(e.target.value);
            if (notes.length) onChange((entry) => void (entry.notes = notes));
          }}
        />
      </Field>
      {lesson.questions.map((question, index) => (
        <div className="content-grid content-row" key={index} data-testid={`${id}-q${index + 1}`}>
          <Field id={`${id}-q${index}`} label={`Question ${index + 1}`}>
            <Input
              id={`${id}-q${index}`}
              value={question.q}
              maxLength={200}
              onChange={(e) => onChange((entry) => void (entry.questions[index]!.q = e.target.value))}
            />
          </Field>
          <Field id={`${id}-q${index}-right`} label="Right answer">
            <Input
              id={`${id}-q${index}-right`}
              value={question.right}
              maxLength={120}
              onChange={(e) => onChange((entry) => void (entry.questions[index]!.right = e.target.value))}
            />
          </Field>
          {question.wrong.map((wrong, w) => (
            <Field key={w} id={`${id}-q${index}-wrong${w}`} label={`Wrong answer ${w + 1}`}>
              <Input
                id={`${id}-q${index}-wrong${w}`}
                value={wrong}
                maxLength={120}
                onChange={(e) => onChange((entry) => void (entry.questions[index]!.wrong[w] = e.target.value))}
              />
            </Field>
          ))}
        </div>
      ))}
    </details>
  );
}
